/** Meta campaigns (C12): helpers shared by the list and the campaign screens. */

import { type AdCampaignStatusEnum, type Metrics, useAdsCampaignsModeRetrieve } from "@agencyos/api-client";
import { Badge, type BadgeTone, GlassCard } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { FlaskConical, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { localeOf } from "../billing/shared";

export const STATUS_TONE: Record<AdCampaignStatusEnum, BadgeTone> = {
  draft: "default",
  paused: "offer",
  active: "success",
  ended: "danger",
};

export function useRefreshAds() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/ads/"),
    });
}

/** « Yaoundé, Douala » → ["Yaoundé", "Douala"]. */
export function splitList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

/** Whether the Meta Marketing integration is live (undefined while loading). */
export function useAdsLive(): boolean | undefined {
  const mode = useAdsCampaignsModeRetrieve();
  return mode.data?.status === 200 ? mode.data.data.live : undefined;
}

/** « Mode test » / « Mode réel »: always visible on the campaign screens. */
export function ModeNotice({ live }: { live: boolean | undefined }) {
  const { t } = useTranslation();
  if (live === undefined) return null;
  return (
    <GlassCard as="div" className={`stack ads-mode${live ? " notice-warning" : ""}`} role="note" aria-label={live ? t("ads.mode.live") : t("ads.mode.test")}>
      <div className="row">
        {live ? <ShieldAlert size={18} aria-hidden="true" /> : <FlaskConical size={18} aria-hidden="true" />}
        <Badge tone={live ? "danger" : "default"}>{live ? t("ads.mode.live") : t("ads.mode.test")}</Badge>
      </div>
      <p className="muted">
        {live ? t("ads.mode.liveText") : t("ads.mode.testText")}{" "}
        <Link className="link" to="/settings/integrations">
          {t("ads.mode.settings")}
        </Link>
      </p>
    </GlassCard>
  );
}

export function roiText(value: number | null | undefined, language: string): string {
  if (value === null || value === undefined) return "—";
  return `${new Intl.NumberFormat(localeOf(language), { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(value)} %`;
}

/** Spend → leads → clients → revenue collected, as computed by the server. */
export function MetricsGrid({ metrics, label }: { metrics: Metrics; label: string }) {
  const { t, i18n } = useTranslation();
  const tiles: [string, string | number][] = [
    [t("ads.roi.spend"), metrics.spend_text],
    [t("ads.roi.leads"), metrics.leads],
    [t("ads.roi.clients"), metrics.clients],
    [t("ads.roi.revenue"), metrics.revenue_text],
    [t("ads.roi.costPerLead"), metrics.cost_per_lead_text || t("ads.roi.none")],
    [t("ads.roi.costPerClient"), metrics.cost_per_client_text || t("ads.roi.none")],
    [t("ads.roi.roi"), roiText(metrics.roi_percent, i18n.language)],
  ];
  return (
    <dl className="kpis ads-kpis" aria-label={label}>
      {tiles.map(([name, value]) => (
        <div key={name} className="kpi ads-kpi">
          <dt className="kpi__label">{name}</dt>
          <dd className="kpi__value">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
